import { useMemo } from 'react';

import meanBy from 'lodash/meanBy';
import round from 'lodash/round';

import { Box, Stack } from '@cast/design-system';
import {
  ChartMetricsRow,
  ChartMetricsRowSkeleton,
} from '@cast/features-cluster-ui';
import { FailedToLoad } from '@cast/shared-messages-ui';
import { useDisabledMetricsBoxes } from '@cast/features-cluster-shared';
import type { TimeUnit } from '@cast/shared-types';

import { ResourceChart } from '../../../_components/ui/ResourceChart';
import { InventoryResourceMetricBox } from '../../../_components/ui/InventoryResourceMetricBox';
import { WidgetLayout } from '../../../_components/ui/WidgetLayout';

type DateTime = string | number | Date;

type ResourceBreakdownChartMetricSettings<K extends string> = Record<
  K,
  {
    color: string;
    title: string;
    tooltipTitle: string;
  }
>;

type ResourceBreakdownChartProps<
  T extends { timestamp: string },
  K extends string
> = {
  title: string;
  testId: string;
  data: T[];
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  refetch: () => void;
  dataKeys: K[];
  chartDataSettings: ResourceBreakdownChartMetricSettings<K>;
  unit: string;
  timeZone: string;
  dateRange?: { from: DateTime; to: DateTime };
  timeUnit?: TimeUnit;
  step?: number;
  filler?: (timestamp: DateTime) => Partial<T>;
  yAxisFormatter?: (value: number) => string;
};

export const ResourceBreakdownChart = <
  T extends { timestamp: string },
  K extends string
>({
  title,
  testId,
  data,
  isLoading,
  isFetching,
  error,
  refetch,
  dataKeys,
  chartDataSettings,
  unit,
  timeZone,
  dateRange,
  timeUnit,
  step,
  filler,
  yAxisFormatter,
}: ResourceBreakdownChartProps<T, K>) => {
  const { toggleMetric, isMetricDisabled } = useDisabledMetricsBoxes<K>();

  const hasError = !!error;
  const isChartLoading = isLoading || (hasError && isFetching);

  const colorMap = dataKeys.reduce((acc, key) => {
    acc[key] = chartDataSettings[key].color;
    return acc;
  }, {} as Record<K, string>);

  const titleMap = dataKeys.reduce((acc, key) => {
    acc[key] = chartDataSettings[key].title;
    return acc;
  }, {} as Record<K, string>);

  const averages = useMemo(() => {
    if (!data?.length) {
      return undefined;
    }

    return dataKeys.reduce((acc, key) => {
      acc[key] = round(meanBy(data, key), 3);
      return acc;
    }, {} as Record<K, number>);
  }, [data, dataKeys]);

  return (
    <WidgetLayout title={title} testId={testId}>
      {hasError && !isFetching ? (
        <FailedToLoad
          title={`Failed to load ${title.toLowerCase()}`}
          refresh={refetch}
        />
      ) : (
        <Stack gap={12} height="100%">
          {isChartLoading ? (
            <ChartMetricsRowSkeleton
              alwaysRow
              gap={16}
              count={dataKeys.length}
              testId={`${testId}-metrics-skeleton`}
            />
          ) : (
            <ChartMetricsRow
              alwaysRow
              alignItems="center"
              gap={16}
              testId={`${testId}-metrics`}
            >
              {dataKeys.map((dataKey) => (
                <InventoryResourceMetricBox
                  key={dataKey}
                  testId={dataKey}
                  dataKey={dataKey}
                  value={averages?.[dataKey]}
                  unit={unit}
                  label={titleMap[dataKey]}
                  colorMap={colorMap}
                  titleMap={titleMap}
                  toggleMetric={toggleMetric}
                  isMetricDisabled={isMetricDisabled}
                  roundBy={0}
                  decimals={3}
                />
              ))}
            </ChartMetricsRow>
          )}

          <Box flex={1} display="flex">
            <ResourceChart
              data={data ?? []}
              isLoading={isChartLoading}
              hasNoData={!data?.length}
              unit={unit}
              isMetricDisabled={(key) => isMetricDisabled(key as K)}
              timeZone={timeZone}
              chartDataSettings={chartDataSettings}
              dateRange={dateRange}
              timeUnit={timeUnit}
              step={step}
              filler={filler}
              yAxisFormatter={yAxisFormatter}
              testId={`${testId}-chart`}
            />
          </Box>
        </Stack>
      )}
    </WidgetLayout>
  );
};
